import {
  createOneCopilotEntry as createStoredOneCopilotEntry,
  openOneCopilot,
  type OneCopilotEntryCreationResult,
  type OneCopilotEntryRequest,
  type OneCopilotEntryTarget,
} from '@github-ui/ahp-session-client/one-copilot-entry'
import {createPreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import type {QueryClient} from '@github-ui/react-query'

import type {ImplicitContextPage} from '../contexts/ImplicitContextPageContext'
import {readCurrentImplicitContextPage} from '../hooks/use-current-implicit-context-page'
import type {ImplicitReference} from './implicit-context'
import {attachmentsForPage, resolveImplicitContextReferences} from './use-implicit-references'

export type OneCopilotContextIntent = 'chat' | 'task'

export interface OneCopilotContextEntryInput {
  intent: OneCopilotContextIntent
  source: string
  queryClient: QueryClient
  /**
   * Read when the handoff starts so the request carries the click-time location,
   * including a current query string or selected-line hash.
   */
  getPage?: () => ImplicitContextPage | null
}

export interface CreateOneCopilotContextEntryInput extends OneCopilotContextEntryInput {
  destination?: string
}

export interface OpenOneCopilotContextEntryInput extends CreateOneCopilotContextEntryInput {
  navigate?: (url: string) => void
  signal?: AbortSignal
}

export type OpenOneCopilotContextEntryResult = OneCopilotEntryCreationResult | {status: 'cancelled'}

function isSupportedContextReference(reference: ImplicitReference): boolean {
  switch (reference.type) {
    case 'repository':
    case 'file':
    case 'file-v2':
    case 'snippet':
    case 'file-diff':
    case 'commit':
    case 'issue':
    case 'discussion':
    case 'pull-request':
    case 'job':
      return true
    case 'release':
      return false
    default:
      return false
  }
}

function targetForPage(intent: OneCopilotContextIntent, page: ImplicitContextPage | null): OneCopilotEntryTarget {
  if (intent === 'chat') {
    return {
      kind: 'chat',
      ...(page?.owner ? {provisioningTarget: {kind: 'scope', owner: page.owner}} : {}),
    }
  }

  const nameWithOwner = page?.owner && page.repo ? `${page.owner}/${page.repo}` : undefined
  return {
    kind: 'task',
    ...(nameWithOwner ? {repository: {nameWithOwner}} : {}),
  }
}

/**
 * Builds a reviewable One Copilot request from the current GitHub page.
 *
 * Releases and unsupported endpoint variants intentionally degrade to the
 * generic page URL instead of being handed to the unified composer.
 */
export async function buildOneCopilotContextEntryRequest({
  intent,
  source,
  queryClient,
  getPage = readCurrentImplicitContextPage,
}: OneCopilotContextEntryInput): Promise<OneCopilotEntryRequest> {
  const page = getPage()
  const references = await resolveImplicitContextReferences(queryClient, page)
  const attachments = attachmentsForPage(references.filter(isSupportedContextReference), page)

  return {
    target: targetForPage(intent, page),
    prompt: createPreparedPrompt({text: '', attachments}),
    source,
    behavior: 'prefill',
  }
}

/** Resolves page context and stores a One Copilot entry without navigating. */
export async function createOneCopilotContextEntry({
  destination,
  ...input
}: CreateOneCopilotContextEntryInput): Promise<OneCopilotEntryCreationResult> {
  const request = await buildOneCopilotContextEntryRequest(input)
  return createStoredOneCopilotEntry(request, destination === undefined ? {} : {destination})
}

/** Resolves page context, stores the One Copilot entry, and navigates to it. */
export async function openOneCopilotContextEntry({
  destination,
  navigate,
  signal,
  ...input
}: OpenOneCopilotContextEntryInput): Promise<OpenOneCopilotContextEntryResult> {
  if (signal?.aborted) return {status: 'cancelled'}

  const request = await buildOneCopilotContextEntryRequest(input)
  if (signal?.aborted) return {status: 'cancelled'}

  return openOneCopilot({
    ...request,
    ...(destination === undefined ? {} : {destination}),
    ...(navigate === undefined ? {} : {navigate}),
  })
}
